export interface FreebeImportScheduleResult {
  readonly connectionStatus:
    'PENDING_AUTHORIZATION' | 'AUTHORIZATION_CAPTURED' | 'ACTIVE' | 'DISABLED' | null;
  readonly accountConfigured: boolean;
  readonly canEnable: boolean;
  readonly enabled: boolean;
  readonly localTime: string;
  readonly timeZone: 'Europe/Paris';
  readonly activeWeekdays?: readonly FreebeImportWeekday[];
  readonly nextRunAt: string | null;
  readonly updatedAt: string | null;
}

export interface SaveFreebeImportScheduleInput {
  readonly enabled: boolean;
  readonly localTime: string;
  readonly timeZone: 'Europe/Paris';
  readonly activeWeekdays?: readonly FreebeImportWeekday[];
}

export type FreebeImportWeekday =
  'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export class FreebeImportScheduleApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string | null,
  ) {
    super('La planification Freebe ne peut pas être enregistrée.');
    this.name = 'FreebeImportScheduleApiError';
  }
}

async function request(
  method: 'GET' | 'POST',
  csrfToken?: string,
  body?: SaveFreebeImportScheduleInput,
): Promise<FreebeImportScheduleResult> {
  const response = await fetch('/api/integrations/freebe/schedule', {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(csrfToken === undefined ? {} : { 'X-CSRF-Token': csrfToken }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    let code: string | null = null;
    try {
      const payload = (await response.json()) as unknown;
      if (typeof payload === 'object' && payload !== null && 'code' in payload) {
        const candidate = (payload as { code?: unknown }).code;
        code = typeof candidate === 'string' ? candidate : null;
      }
    } catch {
      // Le statut HTTP reste exploitable lorsque le corps n'est pas un JSON structuré.
    }
    throw new FreebeImportScheduleApiError(response.status, code);
  }
  return (await response.json()) as FreebeImportScheduleResult;
}

export const freebeImportScheduleApi = {
  read(): Promise<FreebeImportScheduleResult> {
    return request('GET');
  },
  save(
    input: SaveFreebeImportScheduleInput,
    csrfToken: string,
  ): Promise<FreebeImportScheduleResult> {
    return request('POST', csrfToken, input);
  },
};
